import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  BookMarked,
  CalendarClock,
  CheckCircle2,
  Clock3,
  FileCode2,
  Loader,
  MessageSquareText,
  Play,
  Save,
  Settings2,
  ShieldCheck,
  Target,
  Terminal,
  Trash2,
  UserRound,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNowSeconds } from '../hooks/useNowSeconds';
import { useScheduleTargets } from '../hooks/useScheduleTargets';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { TellrawComposer, EMPTY_COMPOSER } from '../components/scheduledcommands/tellraw/TellrawComposer';
import type { ComposerValue } from '../components/scheduledcommands/tellraw/TellrawComposer';
import { LibraryPickerModal, SaveToLibraryModal } from '../components/scheduledcommands/tellraw/LibraryModals';
import { TargetPicker } from '../components/scheduledcommands/TargetPicker';
import { IntervalField, intervalError } from '../components/scheduledcommands/IntervalField';
import { WindowFields, windowProblems } from '../components/scheduledcommands/WindowFields';
import { RunHistory } from '../components/scheduledcommands/RunHistory';
import { RunNowModal } from '../components/scheduledcommands/RunNowModal';
import { PlayerSendModal } from '../components/scheduledcommands/PlayerSendModal';
import { RelativeTime, ScheduleStateBadge } from '../components/scheduledcommands/ScheduleBits';
import { scheduledCommandsApi, ScheduledCommandsApiError } from '../services/scheduledCommandsApi';
import type { RunSummary, Schedule, ScheduleInput, ScheduleTarget } from '../types/scheduledCommands';
import { getScheduledCommandPerms } from '../utils/scheduledCommandPermissions';
import {
  describeSchedule,
  narrowToPlayer,
  reachOf,
  scheduleDisplayName,
  scheduleState,
} from '../utils/scheduledCommands';
import { compileTellraw, defaultCompat, parseTellraw, validateMessage } from '../utils/tellraw';
import { formatReleaseAt } from '../utils/comingSoon';
import { cn } from '../utils/cn';

type Mode = 'tellraw' | 'command';

interface EditorForm {
  name: string;
  mode: Mode;
  /** The console command, in command mode. */
  command: string;
  composer: ComposerValue;
  targets: ScheduleTarget[];
  interval: number;
  enabled: boolean;
  starts_at: number | null;
  ends_at: number | null;
}

const NEW_FORM: EditorForm = {
  name: '',
  mode: 'tellraw',
  command: '',
  composer: EMPTY_COMPOSER,
  targets: [{ type: 'all' }],
  interval: 900,
  enabled: true,
  starts_at: null,
  ends_at: null,
};

/** Refused by the bot too: a timer that deops, ops or stops every server is never what someone meant. */
const FORBIDDEN_COMMANDS = new Set(['op', 'deop', 'stop']);

/** The command's name as the bot reads it: leading slashes and any namespace (`minecraft:stop`) removed. */
function commandName(command: string): string {
  const word = command.trimStart().replace(/^\/+/, '').split(/\s/, 1)[0] ?? '';
  return word.slice(word.lastIndexOf(':') + 1).toLowerCase();
}
const MAX_COMMAND = 32_000;
const SELECTOR = /^(@[aprse](\[[^\]]*\])?|[A-Za-z0-9_]{3,16})$/;
/** Enough of the conversation to keep follow-ups in context next time, without storing a novel. */
const SAVED_TURNS = 24;

function formFromSchedule(schedule: Schedule): EditorForm {
  const tellraw = schedule.editor_state?.kind === 'tellraw' ? schedule.editor_state : null;
  return {
    name: schedule.name ?? '',
    mode: tellraw ? 'tellraw' : 'command',
    command: schedule.command,
    composer: tellraw
      ? { message: tellraw.message, compat: tellraw.compat, selector: tellraw.selector || '@a', conversation: tellraw.conversation ?? [] }
      : EMPTY_COMPOSER,
    targets: schedule.targets,
    interval: schedule.interval_seconds,
    enabled: schedule.enabled && schedule.paused_reason !== 'ended',
    starts_at: schedule.starts_at,
    ends_at: schedule.ends_at,
  };
}

/** Exactly what would be sent to the bot. Dirty-checking compares these, so cosmetic state never counts as a change. */
function toInput(form: EditorForm): ScheduleInput {
  const tellraw = form.mode === 'tellraw';
  const { message, compat, selector, conversation } = form.composer;
  return {
    name: form.name.trim() || null,
    command: tellraw ? compileTellraw(message, compat, selector.trim() || '@a').command : form.command.trim(),
    targets: form.targets,
    interval_seconds: form.interval,
    enabled: form.enabled,
    starts_at: form.starts_at,
    ends_at: form.ends_at,
    editor_state: tellraw
      ? { v: 1, kind: 'tellraw', selector: selector.trim() || '@a', message, compat, conversation: conversation.slice(-SAVED_TURNS) }
      : null,
  };
}

/** Only the fields that changed, so an untouched interval never moves the schedule's slot. */
function diff(next: ScheduleInput, before: ScheduleInput): Partial<ScheduleInput> {
  const patch: Partial<ScheduleInput> = {};
  (Object.keys(next) as (keyof ScheduleInput)[]).forEach((key) => {
    if (JSON.stringify(next[key]) !== JSON.stringify(before[key])) {
      (patch as Record<string, unknown>)[key] = next[key];
    }
  });
  return patch;
}

interface Problems {
  name?: string;
  message?: string;
  targets?: string;
  interval?: string;
  starts?: string;
  ends?: string;
}

function problemsOf(form: EditorForm, input: ScheduleInput, nowSec: number): Problems {
  const problems: Problems = {};
  if (form.name.trim().length > 100) problems.name = 'Keep the name under 100 characters.';
  if (form.mode === 'tellraw') {
    const issues = validateMessage(form.composer.message);
    if (form.composer.message.runs.length === 0) problems.message = 'Write the message first.';
    else if (issues.length) problems.message = issues[0].message;
    else if (!SELECTOR.test(form.composer.selector.trim() || '@a')) problems.message = 'Who sees it must be a selector like @a, or one player name.';
  } else if (!input.command) {
    problems.message = 'Enter the command to run.';
  } else if (FORBIDDEN_COMMANDS.has(commandName(input.command))) {
    problems.message = 'op, deop and stop cannot be scheduled. Run them from a server console instead.';
  }
  if (input.command.length > MAX_COMMAND) problems.message = `The command is ${input.command.length.toLocaleString()} characters; the limit is ${MAX_COMMAND.toLocaleString()}.`;
  if (form.targets.length === 0) problems.targets = 'Choose at least one cluster or server.';
  const interval = intervalError(form.interval);
  if (interval) problems.interval = interval;
  const window = windowProblems(form.starts_at, form.ends_at, nowSec);
  if (window.starts) problems.starts = window.starts;
  if (window.ends && form.enabled) problems.ends = window.ends;
  return problems;
}

const FIELD_SECTION: Record<string, keyof Problems> = {
  name: 'name',
  command: 'message',
  editor_state: 'message',
  targets: 'targets',
  interval_seconds: 'interval',
  starts_at: 'starts',
  ends_at: 'ends',
  enabled: 'ends',
};

const draftKey = (id: string) => `argus:scheduled-command-draft:${id}`;

function readDraft(key: string): EditorForm | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; form: EditorForm };
    // A week-old draft is more likely to confuse than to help.
    if (!parsed?.form || Date.now() - parsed.at > 7 * 86_400_000) return null;
    return parsed.form;
  } catch {
    return null;
  }
}

function writeDraft(key: string, form: EditorForm | null) {
  try {
    if (form) window.localStorage.setItem(key, JSON.stringify({ at: Date.now(), form }));
    else window.localStorage.removeItem(key);
  } catch {
    // Private windows and full storage: the draft is a convenience, never required.
  }
}

/**
 * One schedule, created or edited.
 *
 * A full page rather than a modal: the message, the targets and the timing are
 * each worth room, and a stray click outside a modal should never cost someone
 * a message they spent ten minutes on. Unsaved work is kept in this browser and
 * offered back on return.
 */
export function ScheduledCommandEditorView() {
  const { scheduleId = 'new' } = useParams();
  const [params] = useSearchParams();
  // A new address is a new editor: nothing from the previous schedule may leak into the next.
  const address = `${scheduleId}|${params.get('from') ?? ''}|${params.get('library') ?? ''}`;
  return <ScheduleEditor key={address} scheduleId={scheduleId} params={params} />;
}

function ScheduleEditor({ scheduleId, params }: { scheduleId: string; params: URLSearchParams }) {
  const navigate = useNavigate();
  const location = useLocation();
  const justCreated = !!(location.state as { saved?: boolean } | null)?.saved;
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getScheduledCommandPerms(identity, isDev), [identity, isDev]);
  const now = useNowSeconds(15_000);
  const targets = useScheduleTargets();

  const isNew = scheduleId === 'new';
  const numericId = isNew ? null : Number(scheduleId);
  const fromId = isNew ? Number(params.get('from')) || null : null;
  const libraryId = isNew ? params.get('library') : null;
  const key = draftKey(isNew ? (fromId || libraryId ? `new-${fromId ?? libraryId}` : 'new') : String(numericId));

  const [original, setOriginal] = useState<Schedule | null>(null);
  const [form, setForm] = useState<EditorForm>(NEW_FORM);
  const [baseline, setBaseline] = useState<EditorForm>(NEW_FORM);
  const [loading, setLoading] = useState(!isNew || !!fromId || !!libraryId);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);
  // Compatibility follows the targeted servers' versions until someone sets it by hand.
  const [compatTouched, setCompatTouched] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{ text: string; section?: keyof Problems } | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [showProblems, setShowProblems] = useState(false);

  const [runs, setRuns] = useState<RunSummary[] | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [runsLimit, setRunsLimit] = useState(20);

  const [running, setRunning] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [leaving, setLeaving] = useState<string | null>(null);
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  const [picking, setPicking] = useState(false);

  useDocumentTitle(isNew ? 'New schedule' : original ? scheduleDisplayName(original) : 'Scheduled Command');

  // ── Loading ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (scheduleId === 'library') {
      navigate('/scheduled-commands?tab=library', { replace: true });
      return;
    }
    let live = true;
    const start = (loaded: EditorForm, source: Schedule | null) => {
      if (!live) return;
      setOriginal(source);
      setBaseline(loaded);
      if (justCreated && source) {
        setSavedNote(
          source.enabled && source.next_run_at
            ? `Created. The first run is ${formatReleaseAt(source.next_run_at)}.`
            : 'Created. It is paused, so nothing runs until you switch it on.',
        );
        navigate('.', { replace: true, state: null });
      }
      const draft = readDraft(key);
      if (draft && JSON.stringify(toInput(draft)) !== JSON.stringify(toInput(loaded))) {
        setForm(draft);
        setRestored(true);
        setCompatTouched(true);
      } else {
        setForm(loaded);
        setCompatTouched(!!source?.editor_state);
      }
      setLoading(false);
    };

    (async () => {
      try {
        if (!isNew) {
          if (!Number.isInteger(numericId) || (numericId as number) <= 0) throw new ScheduledCommandsApiError(404, 'There is no schedule at this address.');
          const list = await scheduledCommandsApi.list();
          const found = list.schedules.find((s) => s.id === numericId);
          if (!found) throw new ScheduledCommandsApiError(404, 'This schedule no longer exists. It may have been deleted.');
          start(formFromSchedule(found), found);
        } else if (fromId) {
          const list = await scheduledCommandsApi.list();
          const found = list.schedules.find((s) => s.id === fromId);
          if (!found) throw new ScheduledCommandsApiError(404, 'The schedule to copy no longer exists.');
          const copy = formFromSchedule(found);
          start({ ...copy, name: `Copy of ${scheduleDisplayName(found)}`.slice(0, 100), enabled: false }, null);
        } else if (libraryId) {
          const entry = await scheduledCommandsApi.libraryEntry(libraryId);
          start(
            {
              ...NEW_FORM,
              name: entry.name,
              composer: { message: entry.message, compat: entry.compat, selector: '@a', conversation: entry.conversation ?? [] },
            },
            null,
          );
        } else {
          start(NEW_FORM, null);
        }
      } catch (e) {
        if (live) {
          setLoadError(e instanceof Error ? e.message : 'This schedule could not be loaded.');
          setLoading(false);
        }
      }
    })();
    return () => {
      live = false;
    };
    // Re-run only when the address changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, fromId, libraryId]);

  const loadRuns = useCallback(async () => {
    if (!numericId) return;
    setRunsError(null);
    try {
      setRuns(await scheduledCommandsApi.runs(numericId, runsLimit));
    } catch (e) {
      setRunsError(e instanceof Error ? e.message : 'The run history could not be loaded.');
    }
  }, [numericId, runsLimit]);

  useEffect(() => {
    if (!isNew && perms.canView) void loadRuns();
  }, [isNew, perms.canView, loadRuns]);

  // ── Derived state ────────────────────────────────────────────────────────

  const input = useMemo(() => toInput(form), [form]);
  const baselineInput = useMemo(() => toInput(baseline), [baseline]);
  const dirty = useMemo(() => JSON.stringify(input) !== JSON.stringify(baselineInput), [input, baselineInput]);
  const problems = useMemo(() => problemsOf(form, input, now), [form, input, now]);
  const hasProblems = Object.keys(problems).length > 0;
  const reach = useMemo(() => reachOf(form.targets, targets.catalog), [form.targets, targets.catalog]);
  const versions = useMemo(() => reach.servers.map((s) => s.version), [reach]);
  const summary = describeSchedule(
    { interval_seconds: form.interval, enabled: form.enabled, starts_at: form.starts_at, ends_at: form.ends_at },
    targets.catalog ? reach.servers.length : null,
    now,
  );
  const shown = (section: keyof Problems) => (showProblems || saveError?.section === section ? problems[section] ?? (saveError?.section === section ? saveError.text : undefined) : undefined);
  const commandBytes = useMemo(() => new TextEncoder().encode(input.command).length, [input.command]);
  const commandParse = useMemo(() => (form.mode === 'command' && form.command.trim() ? parseTellraw(form.command) : null), [form.mode, form.command]);

  // Keep "Works on" matched to the targets until someone chooses it themselves.
  useEffect(() => {
    if (compatTouched || !targets.catalog || form.mode !== 'tellraw') return;
    const next = defaultCompat(versions);
    const current = form.composer.compat;
    if (next.legacy !== current.legacy || next.modern !== current.modern || next.current !== current.current) {
      setForm((prev) => ({ ...prev, composer: { ...prev.composer, compat: next } }));
      setBaseline((prev) => (original ? prev : { ...prev, composer: { ...prev.composer, compat: next } }));
    }
  }, [versions, compatTouched, targets.catalog, form.mode, form.composer.compat, original]);

  // Unsaved work survives a closed tab or a stray navigation.
  useEffect(() => {
    if (loading || loadError) return;
    const id = window.setTimeout(() => writeDraft(key, dirty ? form : null), 400);
    return () => window.clearTimeout(id);
  }, [form, dirty, key, loading, loadError]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const update = (patch: Partial<EditorForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setSaveError(null);
    setSavedNote(null);
  };

  const updateComposer = (next: ComposerValue) => {
    if (next.compat !== form.composer.compat) setCompatTouched(true);
    update({ composer: next });
  };

  const setMode = (mode: Mode) => {
    if (mode === form.mode) return;
    if (mode === 'command') {
      // Carry the compiled command across, so switching to edit it by hand starts from what was built.
      const compiled = form.composer.message.runs.length ? input.command : form.command;
      update({ mode, command: compiled });
    } else {
      const parsed = form.command.trim() ? parseTellraw(form.command) : null;
      if (parsed?.ok && form.composer.message.runs.length === 0) {
        update({ mode, composer: { ...form.composer, message: parsed.message, selector: parsed.selector ?? '@a' } });
      } else {
        update({ mode });
      }
    }
  };

  const leave = (to: string) => {
    if (dirty) setLeaving(to);
    else navigate(to);
  };

  const discard = () => {
    writeDraft(key, null);
    setForm(baseline);
    setRestored(false);
    setSaveError(null);
    setShowProblems(false);
  };

  // ── Saving ───────────────────────────────────────────────────────────────

  const save = async () => {
    setShowProblems(true);
    if (hasProblems || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (isNew) {
        const created = await scheduledCommandsApi.create(input);
        writeDraft(key, null);
        navigate(`/scheduled-commands/${created.id}`, { replace: true, state: { saved: true } });
        return;
      }
      const patch = diff(input, baselineInput);
      if (Object.keys(patch).length === 0) {
        setSaving(false);
        return;
      }
      const updated = await scheduledCommandsApi.update(numericId as number, patch);
      const next = formFromSchedule(updated);
      // Keep the conversation the composer had; the bot stores the capped copy.
      if (form.mode === 'tellraw') next.composer = form.composer;
      setOriginal(updated);
      setBaseline(next);
      setForm(next);
      writeDraft(key, null);
      setRestored(false);
      setShowProblems(false);
      setSavedNote(
        updated.next_run_at && updated.enabled
          ? `Saved. The next run is ${formatReleaseAt(updated.next_run_at)}.`
          : 'Saved.',
      );
    } catch (e) {
      const field = e instanceof ScheduledCommandsApiError ? e.field : null;
      setSaveError({
        text: e instanceof Error ? e.message : 'The schedule could not be saved.',
        section: field ? FIELD_SECTION[field] : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!numericId) return;
    setDeleteBusy(true);
    try {
      await scheduledCommandsApi.remove(numericId);
      writeDraft(key, null);
      navigate('/scheduled-commands', { replace: true });
    } catch (e) {
      setSaveError({ text: e instanceof Error ? e.message : 'The schedule could not be deleted.' });
      setDeleteBusy(false);
      setDeleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const title = form.name.trim() || (isNew ? 'New schedule' : original ? scheduleDisplayName(original) : 'Schedule');
  const state = original ? scheduleState(original, now) : null;
  const readOnly = !perms.canWrite;

  if (!perms.canView) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Network Config" title="Scheduled Commands" icon={CalendarClock} backTo="/scheduled-commands" backLabel="Scheduled Commands" />
        <EmptyState
          tone="warning"
          icon={ShieldCheck}
          title="You don't have permission to view scheduled commands"
          description={<>Ask an admin for the <code className="font-mono text-xs">argus.scheduledcommands.read</code> permission.</>}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Network Config" title={isNew ? 'New schedule' : 'Loading…'} icon={CalendarClock} backTo="/scheduled-commands" backLabel="Scheduled Commands" />
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => <SkeletonLoader key={i} className="h-48" />)}
          </div>
          <SkeletonLoader className="h-64" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Network Config" title="Schedule" icon={CalendarClock} backTo="/scheduled-commands" backLabel="Scheduled Commands" />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="This schedule could not be opened"
          description={loadError}
          action={<button type="button" onClick={() => navigate('/scheduled-commands')} className="glass-button">Back to all schedules</button>}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      <PageHeader
        eyebrow="Network Config"
        title={title}
        icon={CalendarClock}
        onBack={() => leave('/scheduled-commands')}
        backLabel="Scheduled Commands"
        titleAdornment={state && <ScheduleStateBadge state={state} />}
        description={
          isNew
            ? 'Choose what to send, where, and how often. Nothing runs until you save.'
            : original && (
                <>
                  Created {original.created_by ? `by ${original.created_by.split('@')[0]} ` : ''}
                  <RelativeTime at={original.created_at} />
                  {original.updated_at && (
                    <>
                      {' '}· changed {original.updated_by ? `by ${original.updated_by.split('@')[0]} ` : ''}
                      <RelativeTime at={original.updated_at} />
                    </>
                  )}
                </>
              )
        }
        actions={
          perms.canWrite && (
            <div className="flex items-center gap-2">
              {original && (
                <>
                  <button type="button" onClick={() => setRunning(true)} className="glass-button" title={dirty ? 'Runs the saved version' : 'Run once now'}>
                    <Play className="w-4 h-4" />
                    <span className="hidden sm:inline">Run now</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSending(true)}
                    className="glass-button"
                    disabled={!/@a/.test(original.command)}
                    title={/@a/.test(original.command) ? 'Send the saved version to one player' : 'Only commands that target @a can be sent to one player'}
                  >
                    <UserRound className="w-4 h-4" />
                    <span className="hidden sm:inline">Send to player</span>
                  </button>
                </>
              )}
              <button type="button" onClick={() => void save()} className="btn-primary" disabled={saving || (!isNew && !dirty)}>
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isNew ? 'Create schedule' : 'Save changes'}
              </button>
            </div>
          )
        }
      />

      {restored && (
        <div className="alert alert-info" role="status">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">Your unsaved changes from last time were restored.</span>
          <button type="button" onClick={discard} className="btn-ghost shrink-0">Discard them</button>
        </div>
      )}
      {savedNote && (
        <div className="alert alert-success" role="status">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span className="flex-1">{savedNote}</span>
          <button type="button" onClick={() => setSavedNote(null)} className="btn-ghost shrink-0">Dismiss</button>
        </div>
      )}
      {saveError && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{saveError.text}</span>
        </div>
      )}
      {readOnly && (
        <div className="alert alert-info" role="status">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          <span>You can look but not change. Editing needs the <code className="font-mono text-xs">argus.scheduledcommands.write</code> permission.</span>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem] items-start">
        <div className="space-y-6 min-w-0">
          {/* Name and on/off */}
          <section className="glass-card p-4 md:p-5">
            <SectionHeader title="Details" icon={Settings2} />
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex-1 min-w-0">
                <label htmlFor="schedule-name" className="block text-xs font-medium text-glass-400 mb-1.5">Name</label>
                <input
                  id="schedule-name"
                  className={cn('glass-input w-full', shown('name') && 'border-red-500/50')}
                  value={form.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="e.g. Store sale announcement"
                  maxLength={100}
                  disabled={readOnly}
                />
                <p className={cn('text-xs mt-1', shown('name') ? 'text-red-400' : 'text-glass-500')}>
                  {shown('name') ?? 'Shown here and in the dispatch log channel. Leave it empty to show the command instead.'}
                </p>
              </div>
              <div className="sm:pt-6">
                <label className={cn('inline-flex items-center gap-3 text-sm text-glass-300', readOnly ? 'cursor-default' : 'cursor-pointer')}>
                  <span className="switch">
                    <input type="checkbox" checked={form.enabled} onChange={(e) => update({ enabled: e.target.checked })} disabled={readOnly} />
                    <span />
                  </span>
                  {form.enabled ? 'On' : 'Paused'}
                </label>
                {original?.paused_reason === 'ended' && !form.enabled && (
                  <p className="text-xs text-glass-500 mt-1 max-w-[14rem]">It ended by date. Move or clear the end date, then switch it on.</p>
                )}
              </div>
            </div>
          </section>

          {/* What to send */}
          <section className="glass-card p-4 md:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-3">
              <SectionHeader title="Message" icon={MessageSquareText} className="mb-0" />
              <div className="segmented" role="radiogroup" aria-label="Kind of command">
                  <button type="button" role="radio" aria-checked={form.mode === 'tellraw'} className="segmented-item segmented-item-sm" data-active={form.mode === 'tellraw'} onClick={() => setMode('tellraw')} disabled={readOnly}>
                    <MessageSquareText className="w-4 h-4" />
                    Tellraw
                  </button>
                  <button type="button" role="radio" aria-checked={form.mode === 'command'} className="segmented-item segmented-item-sm" data-active={form.mode === 'command'} onClick={() => setMode('command')} disabled={readOnly}>
                    <Terminal className="w-4 h-4" />
                    Console command
                  </button>
                </div>
            </div>
            {form.mode === 'tellraw' ? (
              <TellrawComposer
                value={form.composer}
                onChange={updateComposer}
                canWrite={!readOnly}
                targetVersions={targets.catalog ? versions : undefined}
                defaultPlayer={identity?.raw?.custom?.username ?? null}
                actions={
                  !readOnly && (
                    <>
                      <button type="button" onClick={() => setPicking(true)} className="btn-ghost btn-xs">
                        <BookMarked className="w-3.5 h-3.5" />
                        From library
                      </button>
                      <button
                        type="button"
                        onClick={() => setSavingToLibrary(true)}
                        className="btn-ghost btn-xs"
                        disabled={form.composer.message.runs.length === 0}
                      >
                        <BookMarked className="w-3.5 h-3.5" />
                        Save to library
                      </button>
                    </>
                  )
                }
              />
            ) : (
              <div className="space-y-2">
                <label htmlFor="schedule-command" className="sr-only">Console command</label>
                <textarea
                  id="schedule-command"
                  className={cn('glass-input w-full font-mono text-xs leading-5 min-h-[8rem]', shown('message') && 'border-red-500/50')}
                  value={form.command}
                  onChange={(e) => update({ command: e.target.value })}
                  placeholder="say The server restarts in 10 minutes"
                  spellCheck={false}
                  disabled={readOnly}
                  rows={Math.min(16, Math.max(5, form.command.split('\n').length + 1))}
                />
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className={cn(shown('message') ? 'text-red-400' : 'text-glass-500')}>
                    {shown('message') ?? 'Run exactly as typed, from the server console, on every targeted server. No leading slash is needed.'}
                  </span>
                  <span className={cn('tabular-nums', commandBytes > 1446 ? 'text-amber-400' : 'text-glass-500')}>
                    {commandBytes.toLocaleString()} bytes
                  </span>
                </div>
                {commandBytes > 1446 && (
                  <p className="text-xs text-amber-400">
                    Longer than one RCON packet. Servers reached through the RCON fallback (when Quasar does not answer) cannot run it.
                  </p>
                )}
                {commandParse?.ok && !readOnly && (
                  <div className="subcard p-3 flex flex-wrap items-center gap-3">
                    <FileCode2 className="w-4 h-4 text-accent-300 shrink-0" />
                    <span className="flex-1 min-w-0 text-sm text-glass-300">This is a tellraw. The composer can preview it and edit it with the assistant.</span>
                    <button type="button" onClick={() => setMode('tellraw')} className="glass-button glass-button-sm">Open in the composer</button>
                  </div>
                )}
              </div>
            )}
            {form.mode === 'tellraw' && shown('message') && (
              <p className="text-xs text-red-400 mt-3">{shown('message')}</p>
            )}
          </section>

          {/* Who gets it */}
          <section className="glass-card p-4 md:p-5">
            <SectionHeader title="Targets" icon={Target} description="Where it runs. Clusters include servers added to them later." />
            <TargetPicker
              value={form.targets}
              onChange={(next) => update({ targets: next })}
              catalog={targets.catalog}
              loading={targets.loading}
              error={targets.error}
              onRetry={targets.reload}
              disabled={readOnly}
            />
            {shown('targets') && <p className="text-xs text-red-400 mt-2">{shown('targets')}</p>}
          </section>

          {/* When */}
          <section className="glass-card p-4 md:p-5 space-y-5">
            <SectionHeader title="Timing" icon={Clock3} className="mb-0" />
            <IntervalField id="schedule-interval" value={form.interval} onChange={(interval) => update({ interval })} disabled={readOnly} />
            <WindowFields
              startsAt={form.starts_at}
              endsAt={form.ends_at}
              onChange={(next) => update(next)}
              nowSec={now}
              disabled={readOnly}
            />
          </section>
        </div>

        <aside className="space-y-6 xl:sticky xl:top-4">
          <section className="glass-card p-4 md:p-5 space-y-3">
            <SectionHeader title="Summary" icon={CalendarClock} className="mb-0" />
            <p className="text-sm text-white">{summary}</p>
            {targets.catalog && reach.servers.length > 0 && (
              <p className="text-xs text-glass-400">
                <span className="tabular-nums">{reach.online}</span> of <span className="tabular-nums">{reach.servers.length}</span> online now
                {reach.versions.length > 0 && <> · <span className="font-mono">{reach.versions.map((v) => v.version).join(', ')}</span></>}
              </p>
            )}
            {reach.missing.length > 0 && (
              <p className="text-xs text-amber-400">
                {reach.missing.length} target{reach.missing.length === 1 ? ' is' : 's are'} not in the server list any more.
              </p>
            )}
            {original && state === 'active' && original.next_run_at && !dirty && (
              <p className="text-xs text-glass-400">
                Next run <RelativeTime at={original.next_run_at} className="text-glass-300" />
              </p>
            )}
          </section>

          {original && (
            <RunHistory
              runs={runs}
              error={runsError}
              onRetry={() => void loadRuns()}
              onLoadMore={runs && runs.length >= runsLimit && runsLimit < 100 ? () => setRunsLimit(100) : undefined}
            />
          )}

          {original && perms.canWrite && (
            <section className="glass-card p-4 md:p-5">
              <SectionHeader title="Delete" icon={Trash2} iconClassName="text-red-400" description="Stops it for good and removes its run history." />
              <button type="button" onClick={() => setDeleting(true)} className="btn-danger w-full justify-center">
                <Trash2 className="w-4 h-4" />
                Delete schedule
              </button>
            </section>
          )}
        </aside>
      </div>

      {/* Unsaved changes stay in view while scrolling a long message */}
      {perms.canWrite && (dirty || isNew) && (
        <div className="sticky bottom-[calc(5.5rem+env(safe-area-inset-bottom))] md:bottom-4 z-30">
          <div className="glass-card shadow-popover flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
            <p className="flex-1 min-w-0 text-sm text-glass-300">
              {hasProblems && showProblems ? (
                <span className="text-red-400">Fix the marked fields to save.</span>
              ) : (
                summary
              )}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              {dirty && !isNew && (
                <button type="button" onClick={discard} className="glass-button-secondary" disabled={saving}>
                  Discard
                </button>
              )}
              <button type="button" onClick={() => void save()} className="btn-primary" disabled={saving}>
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isNew ? 'Create schedule' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {running && original && (
        <RunNowModal
          schedule={original}
          reachCount={targets.catalog ? reachOf(original.targets, targets.catalog).servers.length : null}
          onClose={() => setRunning(false)}
          onRan={() => void loadRuns()}
        />
      )}

      {sending && original && (
        <PlayerSendModal
          title="Send to one player"
          subtitle="Every @a in the saved command becomes their name."
          defaultPlayer={identity?.raw?.custom?.username ?? null}
          renderPreview={(player) => (
            <pre className="subcard p-3 max-h-40 overflow-auto font-mono text-xs text-glass-300 whitespace-pre-wrap break-all">
              {narrowToPlayer(original.command, player).command}
            </pre>
          )}
          send={(player) => scheduledCommandsApi.run(original.id, player)}
          onClose={() => setSending(false)}
          onSent={() => void loadRuns()}
        />
      )}

      {savingToLibrary && (
        <SaveToLibraryModal
          defaultName={form.name.trim() || 'Untitled message'}
          entry={{
            message: form.composer.message,
            compat: form.composer.compat,
            command: input.command,
            source: form.composer.conversation.length ? 'assistant' : 'manual',
            conversation: form.composer.conversation.length ? form.composer.conversation : null,
          }}
          onClose={() => setSavingToLibrary(false)}
          onSaved={(entry) => {
            setSavingToLibrary(false);
            setSavedNote(`Saved to the tellraw library as ${entry.name}.`);
          }}
        />
      )}

      {picking && (
        <LibraryPickerModal
          replacing={form.composer.message.runs.length > 0}
          onClose={() => setPicking(false)}
          onPick={(entry) => {
            setPicking(false);
            setCompatTouched(true);
            update({
              name: form.name.trim() ? form.name : entry.name,
              composer: { message: entry.message, compat: entry.compat, selector: form.composer.selector || '@a', conversation: entry.conversation ?? [] },
            });
          }}
        />
      )}

      <ConfirmationModal
        isOpen={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => void remove()}
        title={`Delete ${original ? scheduleDisplayName(original) : 'this schedule'}?`}
        message="It stops running and its run history goes with it. This cannot be undone."
        confirmText="Delete schedule"
        isLoading={deleteBusy}
      />

      <ConfirmationModal
        isOpen={leaving !== null}
        onClose={() => setLeaving(null)}
        onConfirm={() => {
          const to = leaving ?? '/scheduled-commands';
          setLeaving(null);
          navigate(to);
        }}
        title="Leave without saving?"
        message="Your changes are not saved. They are kept in this browser for a week, and offered back if you open this schedule again."
        confirmText="Leave"
      />
    </div>
  );
}
